/**
 * auto-textarea.tsx — a <textarea> that grows with its content up to a reasonable
 * cap, then scrolls. It measures `scrollHeight` after collapsing to `auto`, on every
 * input and whenever the controlled `value` changes from outside; the visible cap and
 * the scrollbar are owned by CSS (`.auto-grow` → max-height + overflow-y:auto), so the
 * element never overgrows the layout.
 */
import { useLayoutEffect, useRef } from "react";
import type { TextareaHTMLAttributes } from "react";

function fit(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

export function AutoTextarea({ className, onInput, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  // Re-fit when the controlled value changes (e.g. cleared after submit, or set programmatically).
  useLayoutEffect(() => fit(ref.current), [props.value]);
  return (
    <textarea
      {...props}
      ref={ref}
      className={`field-input auto-grow${className ? ` ${className}` : ""}`}
      onInput={(e) => {
        fit(e.currentTarget);
        onInput?.(e);
      }}
    />
  );
}
